import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { PopoverSide, Rect } from './placement';

/**
 * 浮层作用域。
 *
 * `default`：功能性弹窗走居中 + 模糊背景的模态框。
 * `realtime`：硬实时页面（单机管理）专用 —— **任何浮层都不得渲染背景蒙版**，
 *             模态框降级为页面内嵌的停靠面板。
 *
 * 这个约束由 OverlayProvider 强制执行，业务代码即使写错也不会在实时页上
 * 出现蒙版，因此「单机管理页无蒙版」是可保证的，而不是靠自觉。
 */
export type OverlayScope = 'default' | 'realtime';

export interface PopoverInputOptions {
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
}

export type PopoverKind = 'confirm' | 'alert' | 'prompt';

export interface PopoverOptions {
  kind?: PopoverKind;
  /** 稳定标识。必须是「同一个按钮永远用同一个值」，弹窗位置缓存以它为键。 */
  placementKey: string;
  /** 触发元素。传 `event.currentTarget` 即可。 */
  trigger: HTMLElement | null;
  /**
   * 触发元素的矩形快照，在触发元素先于弹窗卸载时使用。
   *
   * 例如菜单项：点击后菜单立刻关闭，`event.currentTarget` 随即脱离文档，
   * 弹窗就再也量不到按钮位置。这类调用点要在关闭菜单前取一次
   * `getBoundingClientRect()` 传进来，弹窗才会照旧从按钮旁飞出，
   * 而不是退化成屏幕中心。
   */
  anchorRect?: Rect | null;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /**
   * 强调色。
   * `danger` 红色告警（危险操作）、`accent` 蓝色强调（危险但高频，如起飞/降落）、
   * `default` 中性。
   */
  tone?: 'default' | 'danger' | 'accent';
  input?: PopoverInputOptions;
}

export interface OverlayApi {
  /** 确认弹窗。返回用户是否点了确认。 */
  confirm: (options: PopoverOptions) => Promise<boolean>;
  /** 提示弹窗。只有一个确认按钮。 */
  alert: (options: PopoverOptions) => Promise<void>;
  /** 输入弹窗。取消返回 null。 */
  prompt: (options: PopoverOptions) => Promise<string | null>;
  /** 关闭当前所有浮层（路由切换、会话失效时调用）。 */
  dismissAll: () => void;
}

export const OverlayContext = createContext<OverlayApi | null>(null);
export const OverlayScopeContext = createContext<OverlayScope>('default');

export function useOverlay(): OverlayApi {
  const api = useContext(OverlayContext);
  if (!api) throw new Error('useOverlay 必须在 <OverlayProvider> 内部使用');
  return api;
}

export function useOverlayScope(): OverlayScope {
  return useContext(OverlayScopeContext);
}

export interface PlacementCacheEntry {
  bucket: string;
  side: PopoverSide;
}
